import React, { useState, useEffect } from 'react';
import { 
    Camera,
    CheckCircle2,
    AlertCircle,
    Mail,
    LogOut,
    Eye,
    EyeOff,
    Globe,
    Trash2
} from 'lucide-react';
import { useAuth } from '../context/useAuth';
import { apiFetch, apiPut } from '../api';
import { useNotification } from '../context/NotificationContext';
import { Avatar } from '@mui/material';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator.jsx';
import { validatePassword } from '../utils/passwordValidation';
import { processProfileImage } from '../utils/imageProcess';
import { getActiveDisplayPreferences } from '../utils/dateTime.js';
import { Country, State, City } from 'country-state-city';

const ProfilePage = ({ onBack }) => {
    const { user, logout, refreshUser, passwordPolicy, loginWithToken } = useAuth();
    const { addNotification } = useNotification();
    const [loading, setLoading] = useState(false);

    // Profile inputs matching the exact backend-synchronized schema fields in Screenshot 2
    const [profileData, setProfileData] = useState({
        name: '',
        email: '',
        contact_number: '',
        company: '',
        location: '',
        state: '',
        country: '',
        profile_pic: null
    });

    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedState, setSelectedState] = useState('');

    // Preferences State
    const [preferencesData, setPreferencesData] = useState({
        timezone: '',
        date_format: '',
        time_format: ''
    });
    const [activeDefaults, setActiveDefaults] = useState({ timezone: '', date_format: '', time_format: '' });

    // Password State
    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: ''
    });

    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [resetLinkSent, setResetLinkSent] = useState(false);

    const hasChanges = React.useMemo(() => {
        if (!user) return false;
        
        const profileChanged = 
            profileData.name !== (user.name || '') ||
            profileData.contact_number !== (user.contact_number || '') ||
            profileData.company !== (user.company || '') ||
            profileData.location !== (user.location || '') ||
            profileData.state !== (user.state || '') ||
            profileData.country !== (user.country || '');
            
        const prefsChanged =
            preferencesData.timezone !== (user.preferences?.timezone || '') ||
            preferencesData.date_format !== (user.preferences?.date_format || '') ||
            preferencesData.time_format !== (user.preferences?.time_format || '');
            
        return profileChanged || prefsChanged;
    }, [profileData, preferencesData, user]);

    useEffect(() => {
        if (user) {
            const cVal = user.country || '';
            const sVal = user.state || '';
            const lVal = user.location || '';

            // Resolve matched ISO code offline from Country dataset
            const allCountries = Country.getAllCountries();
            const matchedCountry = allCountries.find(
                c => c.name.toLowerCase() === cVal.toLowerCase() || c.isoCode.toLowerCase() === cVal.toLowerCase()
            );
            const countryCode = matchedCountry ? matchedCountry.isoCode : '';

            // Resolve matched State ISO code
            let stateCode = '';
            if (countryCode) {
                const allStates = State.getStatesOfCountry(countryCode);
                const matchedState = allStates.find(
                    s => s.name.toLowerCase() === sVal.toLowerCase() || s.isoCode.toLowerCase() === sVal.toLowerCase()
                );
                stateCode = matchedState ? matchedState.isoCode : '';
            }

            setSelectedCountry(countryCode);
            setSelectedState(stateCode);

            setProfileData({
                name: user.name || '',
                email: user.email || '',
                contact_number: user.contact_number || '',
                company: user.company || '',
                location: lVal,
                state: sVal,
                country: cVal,
                profile_pic: user.profile_pic || null
            });

            const prefs = {
                timezone: user.preferences?.timezone || '',
                date_format: user.preferences?.date_format || '',
                time_format: user.preferences?.time_format || ''
            };
            setPreferencesData(prefs);
            setActiveDefaults(getActiveDisplayPreferences());

        }
    }, [user]);

    const handleProfileUpdate = async (e) => {
        if (e) e.preventDefault();
        setLoading(true);
        try {
            await apiFetch('/users/me', null, {
                method: 'PUT',
                body: JSON.stringify({
                    name: profileData.name,
                    contact_number: profileData.contact_number,
                    profile_pic: profileData.profile_pic,
                    company: profileData.company,
                    location: profileData.location,
                    state: profileData.state,
                    country: profileData.country
                })
            });

            await apiPut('/users/me/preferences', null, {
                timezone: preferencesData.timezone || null,
                date_format: preferencesData.date_format || null,
                time_format: preferencesData.time_format || null
            });

            addNotification("Personal settings and preferences saved successfully", "success");
            if (refreshUser) await refreshUser();
        } catch (err) {
            addNotification(err.message || "Failed to update profile info", "error");
        } finally {
            setLoading(false);
        }
    };

    const passwordValidation = React.useMemo(() => validatePassword(passwordData.new_password, passwordPolicy), [passwordData.new_password, passwordPolicy]);

    const handlePasswordChange = async (e) => {
        if (e) e.preventDefault();
        
        if (!passwordValidation.isValid) {
            addNotification(passwordValidation.errorMessage || 'Password does not meet requirements.', 'error');
            return;
        }

        if (passwordData.new_password !== passwordData.confirm_password) {
            addNotification("New passwords do not match", "error");
            return;
        }

        if (passwordData.new_password === passwordData.current_password) {
            addNotification("New password must be different from current password", "error");
            return;
        }

        setLoading(true);
        try {
            const response = await apiFetch('/auth/change-password', null, {
                method: 'POST',
                body: JSON.stringify({
                    current_password: passwordData.current_password,
                    new_password: passwordData.new_password
                })
            });
            
            if (response && response.access_token && loginWithToken) {
                await loginWithToken(response.access_token, response.sid, true);
            }
            
            addNotification("Your password has been changed successfully.", "success");
            setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
            window.dispatchEvent(new CustomEvent('password-changed'));
        } catch (err) {
            addNotification(err.message || "Failed to change password", "error");
        } finally {
            setLoading(false);
        }
    };



    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            try {
                const { dataUrl, error } = await processProfileImage(file);
                if (error) {
                    addNotification(error, "error");
                    return;
                }
                setProfileData(prev => ({ ...prev, profile_pic: dataUrl }));
                
                // Automatically persist profile image update to backend (use updated payload instead of stale state)
                await apiFetch('/users/me', null, {
                    method: 'PUT',
                    body: JSON.stringify({
                        ...profileData,
                        profile_pic: dataUrl
                    })
                });
                if (refreshUser) await refreshUser();
                addNotification("Profile picture updated", "success");
            } catch (err) {
                console.error("Image processing error:", err);
                addNotification("Failed to process image. Please try another one.", "error");
            }
        }
    };

    const handleRemovePhoto = async () => {
        try {
            setProfileData(prev => ({ ...prev, profile_pic: null }));
            
            // Persist empty profile picture to backend
            await apiFetch('/users/me', null, {
                method: 'PUT',
                body: JSON.stringify({
                    ...profileData,
                    profile_pic: null
                })
            });
            if (refreshUser) await refreshUser();
            addNotification("Profile picture removed", "success");
        } catch (err) {
            addNotification(err.message || "Failed to remove profile picture", "error");
        }
    };

    return (
        <div className="relative flex-1 h-full overflow-y-auto overflow-x-hidden scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden bg-[#F8FAFC] dark:bg-gray-900 font-sans">
            {/* Background Sweep */}
            <div className="absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-[#EFF6FF] dark:from-blue-950/20 via-transparent to-transparent pointer-events-none overflow-hidden">
                <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl opacity-40"></div>
                <div className="absolute top-20 -left-20 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl opacity-40"></div>
            </div>
            
            {/* Split Page Layout Column Wrappers - 100% full-width to sit cleanly adjacent to sidebar */}
            <div className="relative w-full px-6 pt-3 pb-24 sm:px-8 lg:px-10 z-10 flex flex-col">
                


                {/* Main Dynamic Grid Layout (2:1 Grid Ratio for pristine balance) */}
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 items-start w-full">
                    
                    <div className="xl:col-span-2 w-full min-w-0 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.01)]">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white">General Information</h3>
                            
                            {/* Save Changes Button */}
                            {hasChanges && (
                                <button
                                    type="button"
                                    onClick={handleProfileUpdate}
                                    disabled={loading}
                                    className="hover:opacity-90 text-[var(--primary-foreground)] px-6 py-2.5 font-bold text-xs transition-all shadow-[0_4px_12px_rgba(var(--primary-rgb),0.15)] flex items-center gap-2 border-0 cursor-pointer"
                                    style={{ backgroundColor: 'var(--primary-color)', borderRadius: 'var(--border-radius)' }}
                                >
                                    {loading ? 'Saving Changes...' : 'Save Changes'}
                                </button>
                            )}
                        </div>
                        
                        {/* Profile Photo Upload Block */}
                        <div className="flex items-center gap-6 pb-6 border-b border-gray-100 dark:border-gray-700/40 mb-6">
                            <div className="relative">
                                <Avatar 
                                    src={profileData.profile_pic ? (profileData.profile_pic.startsWith('data:') ? profileData.profile_pic : `data:image/png;base64,${profileData.profile_pic}`) : null}
                                    sx={{ 
                                        width: 72, 
                                        height: 72, 
                                        border: '1px solid rgba(0,0,0,0.04)',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
                                        bgcolor: 'primary.main',
                                        fontSize: '1.75rem'
                                    }}
                                >
                                    {profileData.name?.charAt(0).toUpperCase() || profileData.email?.charAt(0).toUpperCase()}
                                </Avatar>
                                <label className="absolute inset-0 bg-black/45 rounded-full flex items-center justify-center cursor-pointer transition-opacity opacity-0 hover:opacity-100">
                                    <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                                    <Camera className="w-4 h-4 text-white" />
                                </label>
                            </div>
                            <div>
                                <div className="flex items-center gap-2.5">
                                    <label 
                                        className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer shadow-sm select-none"
                                        style={{ borderRadius: 'var(--border-radius)' }}
                                    >
                                        <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                                        Upload Photo
                                    </label>
                                    {profileData.profile_pic && (
                                        <button
                                            type="button"
                                            onClick={handleRemovePhoto}
                                            className="inline-flex items-center gap-1.5 px-3.5 py-2 border border-red-200 dark:border-red-900/30 text-xs font-semibold text-red-650 dark:text-red-400 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-950/20 cursor-pointer shadow-sm transition-colors select-none"
                                            style={{ borderRadius: 'var(--border-radius)' }}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                            Remove
                                        </button>
                                    )}
                                </div>
                                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2 leading-tight">
                                    JPG, PNG or GIF • Max size 2MB<br/>Recommended 200 × 200 px
                                </p>
                            </div>
                        </div>

                        {/* Sub-Section 1: PERSONAL INFORMATION */}
                        <div className="mb-8">
                            <h4 className="text-[10px] font-bold text-gray-400 dark:text-gray-500 tracking-widest uppercase mb-4">Personal Information</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2">
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Full Name *</label>
                                    <input
                                        type="text"
                                        value={profileData.name}
                                        onChange={e => setProfileData(p => ({ ...p, name: e.target.value }))}
                                        className="w-full bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm font-sans"
                                    />
                                </div>
                                
                                <div>
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Email Address</label>
                                    <input
                                        type="email"
                                        value={profileData.email}
                                        readOnly
                                        className="w-full bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-400 dark:text-gray-500 focus:outline-none text-sm cursor-not-allowed font-sans"
                                    />
                                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5 flex items-center gap-1 leading-none select-none">
                                        <AlertCircle className="w-3.5 h-3.5" />
                                        Email cannot be changed
                                    </p>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Contact Number</label>
                                    <input
                                        type="text"
                                        value={profileData.contact_number}
                                        onChange={e => setProfileData(p => ({ ...p, contact_number: e.target.value }))}
                                        className="w-full bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm font-sans"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Sub-Section 2: ORGANIZATION & LOCATION */}
                        <div>
                            <h4 className="text-[10px] font-bold text-gray-400 dark:text-gray-500 tracking-widest uppercase mb-4">Organization & Location</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Company / Organization</label>
                                    <input
                                        type="text"
                                        value={profileData.company}
                                        onChange={e => setProfileData(p => ({ ...p, company: e.target.value }))}
                                        className="w-full bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm font-sans"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Country</label>
                                    <select
                                        value={selectedCountry}
                                        onChange={e => {
                                            const cCode = e.target.value;
                                            setSelectedCountry(cCode);
                                            setSelectedState('');
                                            const cObj = Country.getCountryByCode(cCode);
                                            setProfileData(prev => ({
                                                ...prev,
                                                country: cObj ? cObj.name : '',
                                                state: '',
                                                location: ''
                                            }));
                                        }}
                                        className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm font-sans cursor-pointer"
                                    >
                                        <option value="">Select Country</option>
                                        {Country.getAllCountries().map(c => (
                                            <option key={c.isoCode} value={c.isoCode}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1.5">State / Province</label>
                                    <select
                                        value={selectedState}
                                        disabled={!selectedCountry}
                                        onChange={e => {
                                            const sCode = e.target.value;
                                            setSelectedState(sCode);
                                            const sObj = State.getStateByCodeAndCountry(sCode, selectedCountry);
                                            setProfileData(prev => ({
                                                ...prev,
                                                state: sObj ? sObj.name : '',
                                                location: ''
                                            }));
                                        }}
                                        className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm font-sans cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <option value="">Select State</option>
                                        {selectedCountry && State.getStatesOfCountry(selectedCountry).map(s => (
                                            <option key={s.isoCode} value={s.isoCode}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Location / City</label>
                                    {selectedCountry && selectedState && City.getCitiesOfState(selectedCountry, selectedState).length > 0 ? (
                                        <select
                                            value={profileData.location}
                                            onChange={e => {
                                                const cName = e.target.value;
                                                setProfileData(prev => ({
                                                    ...prev,
                                                    location: cName
                                                }));
                                            }}
                                            className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm font-sans cursor-pointer"
                                        >
                                            <option value="">Select City</option>
                                            {City.getCitiesOfState(selectedCountry, selectedState).map(c => (
                                                <option key={c.name} value={c.name}>{c.name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            type="text"
                                            disabled={!selectedState}
                                            value={profileData.location}
                                            onChange={e => setProfileData(p => ({ ...p, location: e.target.value }))}
                                            placeholder={selectedState ? "Enter City Name" : "Select State First"}
                                            className="w-full bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm font-sans disabled:opacity-50 disabled:cursor-not-allowed"
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Sub-Section 3: DISPLAY PREFERENCES */}
                        <div className="mt-8 border-t border-gray-100 dark:border-gray-700/40 pt-8">
                            <div className="flex items-center gap-2.5 mb-4">
                                <Globe className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                <h4 className="text-[10px] font-bold text-gray-400 dark:text-gray-500 tracking-widest uppercase">Display Preferences</h4>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2">
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1.5">
                                        Timezone <span className="text-[10px] font-normal text-gray-400 ml-1">(Leave empty to use tenant or system default)</span>
                                    </label>
                                    <select
                                        value={preferencesData.timezone}
                                        onChange={e => setPreferencesData(p => ({ ...p, timezone: e.target.value }))}
                                        className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm font-sans cursor-pointer"
                                    >
                                        <option value="">Default ({activeDefaults.timezone || 'UTC'})</option>
                                        <option value="UTC">UTC</option>
                                        <option value="America/New_York">America/New_York (Eastern)</option>
                                        <option value="America/Chicago">America/Chicago (Central)</option>
                                        <option value="America/Denver">America/Denver (Mountain)</option>
                                        <option value="America/Los_Angeles">America/Los_Angeles (Pacific)</option>
                                        <option value="Europe/London">Europe/London</option>
                                        <option value="Europe/Paris">Europe/Paris</option>
                                        <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                                        <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                                        <option value="Australia/Sydney">Australia/Sydney (AEST)</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1.5">
                                        Date Format <span className="text-[10px] font-normal text-gray-400 ml-1">(Optional)</span>
                                    </label>
                                    <select
                                        value={preferencesData.date_format}
                                        onChange={e => setPreferencesData(p => ({ ...p, date_format: e.target.value }))}
                                        className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm font-sans cursor-pointer"
                                    >
                                        <option value="">Default ({activeDefaults.date_format || 'YYYY-MM-DD'})</option>
                                        <option value="DD/MM/YYYY">DD/MM/YYYY (e.g. 31/12/2026)</option>
                                        <option value="MM/DD/YYYY">MM/DD/YYYY (e.g. 12/31/2026)</option>
                                        <option value="YYYY-MM-DD">YYYY-MM-DD (e.g. 2026-12-31)</option>
                                        <option value="DD MMM YYYY">DD MMM YYYY (e.g. 31 Dec 2026)</option>
                                        <option value="MMM DD, YYYY">MMM DD, YYYY (e.g. Dec 31, 2026)</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1.5">
                                        Time Format <span className="text-[10px] font-normal text-gray-400 ml-1">(Optional)</span>
                                    </label>
                                    <select
                                        value={preferencesData.time_format}
                                        onChange={e => setPreferencesData(p => ({ ...p, time_format: e.target.value }))}
                                        className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm font-sans cursor-pointer"
                                    >
                                        <option value="">Default ({activeDefaults.time_format === 'hh:mm:ss A' ? '12-hour' : '24-hour'})</option>
                                        <option value="HH:mm:ss">24-hour (e.g. 14:30:00)</option>
                                        <option value="hh:mm:ss A">12-hour (e.g. 02:30:00 PM)</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* ── RIGHT COLUMN: SECURITY & PASSWORD ────────────────── */}
                    <div className="w-full min-w-0 flex flex-col gap-6 font-sans">
                        
                        {/* Change Password Card */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.015)]">
                            <h3 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white mb-6">Change password</h3>
                            
                            <form onSubmit={handlePasswordChange} className="space-y-4">
                                <div className="space-y-4">
                                    {/* Old Password */}
                                    <div>
                                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Old password</label>
                                        <div className="relative">
                                            <input
                                                type={showOldPassword ? "text" : "password"}
                                                value={passwordData.current_password}
                                                onChange={e => setPasswordData(p => ({ ...p, current_password: e.target.value }))}
                                                placeholder="Old password"
                                                required
                                                className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 pr-10"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowOldPassword(!showOldPassword)}
                                                className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                            >
                                                {showOldPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* New Password */}
                                    <div>
                                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1.5">New password</label>
                                        <div className="relative">
                                            <input
                                                type={showNewPassword ? "text" : "password"}
                                                value={passwordData.new_password}
                                                onChange={e => setPasswordData(p => ({ ...p, new_password: e.target.value }))}
                                                placeholder="New password"
                                                required
                                                className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 pr-10"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                            >
                                                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                        <div className="mt-2.5">
                                            <PasswordStrengthIndicator
                                                password={passwordData.new_password}
                                                policy={passwordPolicy}
                                            />
                                        </div>
                                    </div>

                                    {/* Repeat New Password */}
                                    <div>
                                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Repeat New Password</label>
                                        <div className="relative">
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                value={passwordData.confirm_password}
                                                onChange={e => setPasswordData(p => ({ ...p, confirm_password: e.target.value }))}
                                                placeholder="Confirm new password"
                                                required
                                                className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 pr-10"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                            >
                                                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-4">
                                    <button 
                                        type="submit" 
                                        disabled={loading || !passwordValidation.isValid} 
                                        className="w-full hover:opacity-90 text-[var(--primary-foreground)] py-2.5 transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed border-0 shadow-sm"
                                        style={{ backgroundColor: 'var(--primary-color)', borderRadius: 'var(--border-radius)' }}
                                    >
                                        {loading ? 'Processing...' : 'Update Password'}
                                    </button>
                                </div>
                            </form>

                            {/* Divider (Divident) */}
                            <div className="relative my-6 flex items-center justify-center">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-100 dark:border-gray-700/60"></div>
                                </div>
                                <span className="relative bg-white dark:bg-gray-800 px-3 text-[10px] font-extrabold text-gray-400 dark:text-gray-500 uppercase tracking-wider">OR</span>
                            </div>

                            {/* Direct Send Reset Link Block */}
                            <div className="space-y-4">
                                <div className="text-center">
                                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-3 leading-relaxed">
                                        Forgot your password? We can send a secure reset link to your verified email address.
                                    </p>
                                    <button
                                        type="button"
                                        disabled={loading || resetLinkSent}
                                        onClick={async () => {
                                            setLoading(true);
                                            try {
                                                await apiFetch('/auth/forgot-password', null, {
                                                    method: 'POST',
                                                    body: JSON.stringify({ email: user?.email })
                                                });
                                                setResetLinkSent(true);
                                                addNotification("Password reset email sent", "success");
                                            } catch (err) {
                                                addNotification(err.message || "Failed to send reset link. Please try again.", "error");
                                            } finally {
                                                setLoading(false);
                                            }
                                        }}
                                        className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-750 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 shadow-sm transition-all select-none disabled:opacity-50"
                                        style={{ borderRadius: 'var(--border-radius)' }}
                                    >
                                        <Mail className="w-3.5 h-3.5" />
                                        {loading ? 'Sending...' : (resetLinkSent ? 'Reset Link Sent!' : 'Send Reset Link')}
                                    </button>
                                </div>

                                {resetLinkSent && (
                                    <div className="p-3 bg-green-50/50 dark:bg-green-950/20 border border-green-150 dark:border-green-900/30 rounded-lg flex items-start gap-2.5">
                                        <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                                        <p className="text-[11px] text-green-700 dark:text-green-400 leading-normal font-medium">
                                            A secure password reset link has been sent to your verified email address: <strong>{user?.email}</strong>. Please check your inbox.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Session Authorization Card */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.015)]">
                            <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3">Session settings</h3>
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-6 leading-relaxed">
                                Securely log out of your current workspace session across all matching devices.
                            </p>
                            <button
                                type="button"
                                onClick={logout}
                                className="w-full bg-red-50 hover:bg-red-100/80 dark:bg-red-950/20 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 font-bold text-xs py-3 transition-all border-0 shadow-sm flex items-center justify-center gap-2"
                                style={{ borderRadius: 'var(--border-radius)' }}
                            >
                                <LogOut className="w-3.5 h-3.5" />
                                Sign Out of Session
                            </button>
                        </div>

                    </div>

                </div>

            </div>
        </div>
    );
};

export default ProfilePage;
